import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/shared/SEOHead";
import { MapPin, Briefcase, Building2, FileText, HelpCircle, Phone, ChevronRight, Globe } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const staticSections = [
  {
    title: "Company",
    icon: Building2,
    links: [
      { name: "Home", href: "/" },
      { name: "About Us", href: "/about" },
      { name: "Our Services", href: "/services" },
      { name: "Our Process", href: "/our-process" },
      { name: "Gallery", href: "/gallery" },
    ],
  },
  {
    title: "Dealers",
    icon: Briefcase,
    links: [
      { name: "Become a Dealer", href: "/become-dealer" },
      { name: "Open a Store", href: "/open-a-store" },
    ],
  },
  {
    title: "Resources",
    icon: FileText,
    links: [
      { name: "Blog", href: "/blog" },
      { name: "Careers", href: "/careers" },
      { name: "FAQs", href: "/faqs" },
    ],
  },
  {
    title: "Contact",
    icon: Phone,
    links: [
      { name: "Contact Us", href: "/contact" },
    ],
  },
];

export default function SitemapPage() {
  const [locations, setLocations] = useState([]);
  const [stateGroups, setStateGroups] = useState({});

  useEffect(() => {
    fetch(`${API}/api/locations?active=true`)
      .then((r) => r.json())
      .then((data) => {
        const locs = Array.isArray(data) ? data : [];
        setLocations(locs);
        const groups = {};
        locs.forEach((loc) => {
          const state = loc.state || "Other";
          if (!groups[state]) groups[state] = [];
          groups[state].push(loc);
        });
        setStateGroups(groups);
      })
      .catch(() => {});
  }, []);

  const stateNames = Object.keys(stateGroups).sort();

  return (
    <>
      <SEOHead
        title="Site Map | Prime Media Wireless"
        description="Navigate all pages on Prime Media Wireless — store locations, careers, dealer programs, and more."
        path="/sitemap"
      />

      <div className="min-h-screen bg-slate-50" data-testid="sitemap-page">
        {/* Header */}
        <div className="bg-navy py-12">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="w-8 h-8 text-teal" />
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white">
                Site Map
              </h1>
            </div>
            <p className="text-slate-300 font-body max-w-2xl">
              Find your way around Prime Media Wireless. Browse all our pages, store locations, career opportunities, and dealer resources.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-10">
          {/* Main Sections Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="sitemap-sections">
            {staticSections.map((section) => (
              <div key={section.title} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-4">
                  <section.icon className="w-5 h-5 text-teal" />
                  <h2 className="font-heading font-semibold text-navy text-lg">{section.title}</h2>
                </div>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        className="group flex items-center gap-1.5 text-sm font-body text-slate-600 hover:text-teal transition-colors"
                        data-testid={`sitemap-link-${link.href.replace(/\//g, '-').slice(1) || 'home'}`}
                      >
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal transition-colors" />
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Store Locations */}
          {stateNames.length > 0 && (
            <div className="mb-12" data-testid="sitemap-locations">
              <div className="flex items-center gap-2 mb-6">
                <MapPin className="w-6 h-6 text-teal" />
                <h2 className="font-heading font-bold text-navy text-2xl">Store Locations</h2>
                <span className="ml-2 px-2 py-0.5 bg-teal/10 text-teal text-xs font-semibold rounded-full">
                  {locations.length} stores
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {stateNames.map((state) => (
                  <div key={state} className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-heading font-semibold text-navy mb-3 flex items-center justify-between">
                      {state}
                      <span className="text-xs text-slate-400 font-body">{stateGroups[state].length} stores</span>
                    </h3>
                    <ul className="space-y-1.5">
                      {stateGroups[state]
                        .sort((a, b) => (a.city || "").localeCompare(b.city || ""))
                        .map((loc) => (
                        <li key={loc.id}>
                          <Link
                            to={`/locations/${loc.slug || ""}`}
                            className="group flex items-start gap-1.5 text-sm font-body text-slate-600 hover:text-teal transition-colors"
                          >
                            <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal mt-0.5 flex-shrink-0 transition-colors" />
                            <span>{loc.name || `${loc.city}, ${loc.state}`}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* State Dealer Pages */}
          <div className="mb-12" data-testid="sitemap-states">
            <div className="flex items-center gap-2 mb-6">
              <Briefcase className="w-6 h-6 text-teal" />
              <h2 className="font-heading font-bold text-navy text-2xl">Open a Store by State</h2>
              <span className="ml-2 px-2 py-0.5 bg-teal/10 text-teal text-xs font-semibold rounded-full">50 states</span>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {[
                  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
                  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
                  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
                  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
                  "New Hampshire","New Jersey","New Mexico","New York","North Carolina",
                  "North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
                  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
                  "Virginia","Washington","West Virginia","Wisconsin","Wyoming"
                ].map((state) => {
                  const slug = state.toLowerCase().replace(/\s+/g, "");
                  return (
                    <Link
                      key={slug}
                      to={`/${slug}`}
                      className="group flex items-center gap-1 text-sm font-body text-slate-600 hover:text-teal py-1 transition-colors"
                    >
                      <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-teal transition-colors" />
                      {state}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* XML Sitemap link */}
          <div className="text-center text-sm text-slate-400 font-body pb-8">
            <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-teal transition-colors">
              View XML Sitemap
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
